import { useCallback, useEffect } from 'react';
import { Stack, Group, Radio, Box, createStyles } from '@mantine/core';

import { Text } from '~/domains/design-system/Text';
import { toSafeMarkup } from '~/domains/common/utils/toSafeMarkup';

import type { StaticFieldProps } from '../../types/common';
import { FieldMeta } from '../common/FieldMeta';
import { getFilteredSelectOptions } from '../utils/getFilteredSelectOptions';
import useHandleTextClick from '../../../hooks/useHandleTextClick';

import type { RadioGroupField, RadioGroupOption } from './types';

const useStyles = createStyles((theme) => ({
  description: {
    color: theme.colors.gray2[7],
    '& a': {
      color: theme.colorScheme === 'dark' ? theme.colors.splash[5] : theme.colors.splash[7],
      textDecoration: 'none',

      '&:hover': {
        textDecoration: 'underline',
      },
    },
  },
}));

export function RadioGroupFieldElement({
  field,
  path = [],
  value,
  onValueChange,
  errors,
  isTouched,
  sectionKey,
  allValues,
  allErrors,
  propagateFieldLabelToParent,
  FieldSwitch,
}: StaticFieldProps<RadioGroupField>) {
  const { classes } = useStyles();
  const options = getFilteredSelectOptions({
    options: field.options,
    sectionKey,
    allValues,
    allErrors,
  }) as RadioGroupOption[];

  const handleSubLabelClick = useHandleTextClick();

  useEffect(() => {
    if (typeof field.defaultValue !== 'undefined' && typeof value === 'undefined' && !isTouched) {
      onValueChange?.(field.defaultValue, true);
    }
  }, [field.defaultValue, value, isTouched, onValueChange]);

  const handleOptionChange = useCallback(
    (optionValue: string) => {
      const actualValue = options.find((opt) => opt.value.toString() === optionValue)?.value;
      if (actualValue !== undefined) {
        onValueChange?.(actualValue);
      }
    },
    [onValueChange, options]
  );

  const handleOptionClick = useCallback(
    (option: RadioGroupOption) => {
      if (option.isDisabled) return;
      onValueChange?.(option.value);
    },
    [onValueChange]
  );

  if (options.length === 0) {
    return null;
  }

  const radioGroupValue = value !== undefined && value !== null ? value.toString() : '';
  return (
    <div>
      <FieldMeta info={field?.info} field={field} errors={errors} isTouched={isTouched} />
      <Radio.Group value={radioGroupValue} onChange={handleOptionChange}>
        <Stack /* eslint-disable-line local-rules/use-theme-tokens */ spacing={field.params?.spacing ?? 40}>
          {options.map((option) => (
            <Box key={option.value}>
              <Group /* eslint-disable-line local-rules/use-theme-tokens */ spacing={16} align="flex-start" noWrap>
                <Radio
                  color="blue.8"
                  value={option.value.toString()}
                  disabled={option.isDisabled}
                  size="xs"
                  pos="relative"
                  top={2}
                />
                <Stack
                  /* eslint-disable-line local-rules/use-theme-tokens */ spacing={4}
                  sx={{
                    cursor: option.isDisabled ? 'not-allowed' : 'pointer',
                  }}
                  onClick={() => handleOptionClick(option)}
                >
                  <Text variant="title-large">{option.label}</Text>
                  {option.description && (
                    <Text
                      className={classes.description}
                      onClick={handleSubLabelClick}
                      dangerouslySetInnerHTML={{ __html: toSafeMarkup(option.description) }}
                      variant="body-large"
                      c="gray2.3"
                    />
                  )}
                </Stack>
              </Group>

              {option.fields && option.fields.length > 0 && (
                <Stack /* eslint-disable-line local-rules/use-theme-tokens */ spacing={24} mt={20} ml={{ base: 0, md: 36 }}>
                  {option.fields.map((fieldKey) => (
                    <FieldSwitch
                      key={fieldKey}
                      fieldKey={fieldKey}
                      sectionKey={sectionKey || ''}
                      path={path.slice(0, -1)}
                      propagateFieldLabelToParent={propagateFieldLabelToParent}
                    />
                  ))}
                </Stack>
              )}
            </Box>
          ))}
        </Stack>
      </Radio.Group>
    </div>
  );
}
